import React, { ReactElement } from 'react'
import {
  Autocomplete,
  Badge,
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import SortIcon from '@mui/icons-material/Sort'
import { LifecycleFilterValue } from './lifecycle/lifecycle-filter'

export type SortDirection = 'asc' | 'desc'

export type WorkflowFilter =
  'all' | 'needs-revision' | 'in-planning' | 'ready-for-review' | 'approved'

export type MigrationFilter = 'all' | 'needs-migration' | 'v2'

export type WorkflowCounts = {
  needsRevision: number
  inPlanning: number
  readyForReview: number
  approved: number
  all: number
}

type InstructorOption = {
  id: string
  name: string
  credentials?: string
}

type PipelineFilterBarProps = {
  search: string
  onSearchChange: (value: string) => void
  searchPlaceholder?: string

  status: WorkflowFilter
  onStatusChange: (value: WorkflowFilter) => void
  statusCounts: WorkflowCounts

  lifecycle?: LifecycleFilterValue
  onLifecycleChange?: (value: LifecycleFilterValue) => void
  lifecycleCounts?: { all: number; preArchive: number; archive: number }

  instructorFilter?: string | null
  onInstructorChange?: (instructorId: string | null) => void
  instructors?: InstructorOption[]

  migration?: MigrationFilter
  onMigrationChange?: (value: MigrationFilter) => void
  migrationCounts?: { all: number; needsMigration: number; v2: number }

  sort?: SortDirection
  onSortChange?: (value: SortDirection) => void
}

let STATUS_TABS: {
  value: WorkflowFilter
  label: string
  countKey: keyof WorkflowCounts
}[] = [
  {
    value: 'needs-revision',
    label: 'Needs Revision',
    countKey: 'needsRevision',
  },
  { value: 'in-planning', label: 'In Planning', countKey: 'inPlanning' },
  { value: 'ready-for-review', label: 'Ready', countKey: 'readyForReview' },
  { value: 'approved', label: 'Approved', countKey: 'approved' },
  { value: 'all', label: 'All', countKey: 'all' },
]

export default function PipelineFilterBar({
  search,
  onSearchChange,
  searchPlaceholder = 'Search…',
  status,
  onStatusChange,
  statusCounts,
  lifecycle,
  onLifecycleChange,
  lifecycleCounts,
  instructorFilter,
  onInstructorChange,
  instructors,
  migration,
  onMigrationChange,
  migrationCounts,
  sort,
  onSortChange,
}: PipelineFilterBarProps): ReactElement {
  let selectedInstructor =
    instructors?.find((i) => i.id === instructorFilter) ?? null
  let hasSecondaryFilters =
    lifecycle !== undefined ||
    instructors !== undefined ||
    migration !== undefined

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          size="small"
          fullWidth
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{ bgcolor: 'background.paperDark' }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: 'text.disabled' }}
                  />
                </InputAdornment>
              ),
            },
          }}
          aria-label={searchPlaceholder}
        />
        {onSortChange && (
          <Tooltip
            title={sort === 'asc' ? 'Sort newest first' : 'Sort oldest first'}
          >
            <IconButton
              size="small"
              onClick={() => onSortChange(sort === 'asc' ? 'desc' : 'asc')}
              aria-label={
                sort === 'asc' ? 'Sort newest first' : 'Sort oldest first'
              }
              sx={{
                bgcolor: 'background.paperDark',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                transform: sort === 'desc' ? 'scaleY(-1)' : 'none',
              }}
            >
              <SortIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Tabs
        value={status}
        onChange={(_, v) => onStatusChange(v)}
        variant="scrollable"
        scrollButtons={false}
        aria-label="Filter by workflow status"
        sx={{
          minHeight: 36,
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTabs-flexContainer': { gap: 0 },
          '& .MuiTab-root': {
            minHeight: 36,
            minWidth: 0,
            px: 2,
            py: 1,
            fontSize: 12,
            textTransform: 'none',
            lineHeight: 1.3,
            overflow: 'visible',
          },
          '& .MuiTabs-scroller': { overflow: 'visible !important' },
          '& .MuiTabs-scrollButtons': { display: 'none' },
        }}
      >
        {STATUS_TABS.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={
              <Badge
                badgeContent={statusCounts[tab.countKey]}
                color={
                  tab.value === 'needs-revision' &&
                  statusCounts.needsRevision > 0
                    ? 'error'
                    : 'default'
                }
                max={99}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: 9,
                    height: 14,
                    minWidth: 14,
                    top: -2,
                    right: -6,
                    bgcolor:
                      tab.value === 'needs-revision' &&
                      statusCounts.needsRevision > 0
                        ? undefined
                        : 'action.selected',
                    color:
                      tab.value === 'needs-revision' &&
                      statusCounts.needsRevision > 0
                        ? undefined
                        : 'text.secondary',
                  },
                }}
              >
                <Box component="span" sx={{ pr: '14px' }}>
                  {tab.label}
                </Box>
              </Badge>
            }
          />
        ))}
      </Tabs>

      {hasSecondaryFilters && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {migration !== undefined && onMigrationChange && (
            <Select
              size="small"
              value={migration}
              onChange={(e) =>
                onMigrationChange(e.target.value as MigrationFilter)
              }
              aria-label="Filter by migration status"
              sx={{ flex: 1, bgcolor: 'background.paperDark' }}
            >
              <MenuItem value="all">
                All migration statuses
                {migrationCounts ? ` (${migrationCounts.all})` : ''}
              </MenuItem>
              <MenuItem value="needs-migration">
                Needs migration
                {migrationCounts ? ` (${migrationCounts.needsMigration})` : ''}
              </MenuItem>
              <MenuItem value="v2">
                V2{migrationCounts ? ` (${migrationCounts.v2})` : ''}
              </MenuItem>
            </Select>
          )}

          {lifecycle !== undefined && onLifecycleChange && (
            <Select
              size="small"
              value={lifecycle}
              onChange={(e) =>
                onLifecycleChange(e.target.value as LifecycleFilterValue)
              }
              aria-label="Filter by lifecycle state"
              sx={{ flex: 1, bgcolor: 'background.paperDark' }}
            >
              <MenuItem value="all">
                Active{lifecycleCounts ? ` (${lifecycleCounts.all})` : ''}
              </MenuItem>
              <MenuItem value="preArchive">
                Pre-archive
                {lifecycleCounts ? ` (${lifecycleCounts.preArchive})` : ''}
              </MenuItem>
              <MenuItem value="archive">
                Archived{lifecycleCounts ? ` (${lifecycleCounts.archive})` : ''}
              </MenuItem>
            </Select>
          )}

          {instructors !== undefined && onInstructorChange && (
            <Autocomplete
              size="small"
              options={instructors}
              value={selectedInstructor}
              getOptionLabel={(o) =>
                o.credentials ? `${o.name} (${o.credentials})` : o.name
              }
              isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_, v) => onInstructorChange(v?.id ?? null)}
              sx={{ flex: 1 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="All instructors"
                  size="small"
                  sx={{ bgcolor: 'background.paperDark' }}
                  aria-label="Filter by instructor"
                />
              )}
              slotProps={{ paper: { sx: { bgcolor: 'background.paperDark' } } }}
            />
          )}
        </Box>
      )}
    </Box>
  )
}
